<template>
  <el-drawer title="Experiences" v-model="localExperienceDialogVisible" :append-to-body="true" direction="btt"
    :destroy-on-close="true">
    <el-form ref="editForm">
      <el-form-item>
        <div v-for="(exp, index) in experiences" :key="index" class="experience-input text-left">
          <div class="full-width">
            <el-input 
              v-model="exp.title" 
              placeholder="Title"
              maxlength="150"
              show-word-limit
              @blur="exp.dirty.title = true"
              :class="{ 'error': exp.dirty?.title && $v.experiences.$each.$response.$data[index].title.$error }">
            </el-input>
            <el-form-item-error v-if="exp.dirty?.title && $v.experiences.$each.$response.$data[index].title.$error">
              {{ $v.experiences.$each.$response.$errors[index].title[0].$message }}
            </el-form-item-error>
          </div>
          <div>
            <el-date-picker 
              v-model="exp.dateStart" 
              type="date" 
              placeholder="Select start date"
              :format="$filters.systemDateFormat()" :editable="true" value-format="YYYY-MM-DD"
              @blur="exp.dirty.dateStart = true"
              :class="{ 'error': exp.dirty?.dateStart && $v.experiences.$each.$response.$data[index].dateStart.$error }"
              @change="handleDateChange(exp, 'dateStart')"
              @clear="handleDateClear(exp, 'dateStart')">
            </el-date-picker>
            <el-form-item-error v-if="exp.dirty?.dateStart && $v.experiences.$each.$response.$data[index].dateStart.$error">
              {{ $v.experiences.$each.$response.$errors[index].dateStart[0].$message }}
            </el-form-item-error>
          </div>
          <div>
            <el-date-picker 
              v-model="exp.dateEnd" 
              type="date" 
              placeholder="Select end date"
              :format="$filters.systemDateFormat()" :editable="true" value-format="YYYY-MM-DD"
              @blur="exp.dirty.dateEnd = true"
              :class="{ 'error': exp.dirty?.dateEnd && $v.experiences.$each.$response.$data[index].dateEnd.$error }"
              @change="handleDateChange(exp, 'dateEnd')"
              @clear="handleDateClear(exp, 'dateEnd')">
            </el-date-picker>
            <el-form-item-error v-if="exp.dirty?.dateEnd && $v.experiences.$each.$response.$data[index].dateEnd.$error">
              {{ $v.experiences.$each.$response.$errors[index].dateEnd[0].$message }}
            </el-form-item-error>
          </div>
          <el-button type="danger" @click="removeExperience(index)">
            <TrashIcon />
          </el-button>
        </div>
        <el-button type="primary" class="add-experience-btn" @click="addExperience">Add</el-button>
      </el-form-item>
    </el-form>
    <div slot="footer" class="d-flex gap-1 my-3"  >
      <el-button type="primary" class="mt-0" @click="saveExperiences"
        :disabled="isSaveButtonDisabled">Save</el-button>
      <el-button class="secondary mt-0" @click="closeExperienceDialog">Cancel</el-button>
    </div>
  </el-drawer>
</template>
<script setup>
import { ref, reactive, defineProps, defineEmits, watch, toRefs, computed, onBeforeMount } from 'vue'
import { useStore } from 'vuex'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useVuelidate } from '@vuelidate/core'
import { helpers } from '@vuelidate/validators'
import TrashIcon from '@/components/Icons/TrashIcon.vue'
import { isDateValid } from '@/helpers/date/dateHelpers'

// Store and Props
const store = useStore();
const userData = ref(store.getters.GET_USER_DATA ? store.getters.GET_USER_DATA : {});
const userRole = userData.value.role;

const userExperiences = computed(() => {
  return Array.isArray(userData.value.settings?.hrFlowData?.experiences)
    ? userData.value.settings?.hrFlowData?.experiences
    : Array.isArray(userData.value.locumAsJobSeekerSetting?.hrFlowData?.experiences)
    ? userData.value.locumAsJobSeekerSetting?.hrFlowData?.experiences
    : [];
});

const props = defineProps({
  experienceDialogVisible: {
    type: Boolean,
    required: false
  }
});

// Emit event
const emit = defineEmits(['closeExperienceDrawer']);

// Reactive Form Data
const editExperienceForm = reactive({
  experiences: []
});

// Break the reactivity chain to avoid constructor error
const { experiences } = toRefs(editExperienceForm);

const formatDate = (dateStr) => {
  //if empty return empty string
  if (!dateStr) {
    return '';
  }
  if (dateStr.includes("T")) {
    return dateStr.split("T")[0];
  } else {
    if (isDateValid(dateStr)) {
      return dateStr;
    }
    return '';
  }
}

// Fetch user data when drawer is opened, added this to fix the issue of not updating the experiences list after adding a new experience
const fetchUserData = async () => {
  try {
    userData.value = store.getters.GET_USER_DATA ? store.getters.GET_USER_DATA : {};
    userExperiences.value = userData.value.settings?.hrFlowData?.experiences || userData.value.locumAsJobSeekerSetting?.hrFlowData?.experiences || [];
    // If there are experience, format date fields
    if (!Array.isArray(userExperiences.value)) {
      console.error("userExperiences.value is not an array:", userExperiences.value);
      userExperiences.value = [];
    }

    // If there are experiences, format date fields
    userExperiences.value = userExperiences.value.map(exp => ({
      ...exp,
      dateStart: formatDate(exp.dateStart),
      dateEnd: formatDate(exp.dateEnd),
      title: exp.title || exp.company || ''
    }));

    console.log("After Mapping: ", userExperiences.value);

    editExperienceForm.experiences = [...userExperiences.value];
  } catch (error) {
    ElMessage({ type: 'error', message: error.message });
  }
};

// Validation rules
const rules = computed(() => {
  return {
    experiences: {
      $each: helpers.forEach({
        title: {
          required: helpers.withMessage('Title is required', value => value.trim() !== '')
        },
        dateStart: {
          required: helpers.withMessage('Start date is required', value => value.trim() !== '')
        },
        dateEnd: {
          required: helpers.withMessage('End date is required', value => value.trim() !== ''),
          custom: helpers.withMessage('End date must be greater than start date', (value, parent) => {
            if (value && parent.dateStart) {
              return new Date(value) > new Date(parent.dateStart);
            }
            return true;
          })
        },
      })
    }
  };
});

// Initialize Vuelidate
const $v = useVuelidate(rules, editExperienceForm);

// Watchers to Sync Drawer Visibility
const localExperienceDialogVisible = ref(props.experienceDialogVisible);

watch(localExperienceDialogVisible, (newVal) => {
  if (!newVal) closeExperienceDialog();
});

watch(() => props.experienceDialogVisible, (newVal) => {
  localExperienceDialogVisible.value = newVal;
  // if newVal is true, get updated user data and set experiences
  if (newVal) {
    fetchUserData();
  }
});

onBeforeMount(() => {
  fetchUserData();
});

// Functions
const addExperience = () => {
  experiences.value.push({ title: '', dateStart: '', dateEnd: '', dirty: { title: false, dateStart: false, dateEnd: false }  });
};

const removeExperience = (index) => {
  experiences.value.splice(index, 1);
};

const isSaveButtonDisabled = computed(() => {
  return $v.value.$pending || $v.value.$error || $v.value.$invalid;
});

const saveExperiences = async () => {

  const isValid = await $v.value.$validate();

  if (!isValid) {
    ElMessage({ type: 'error', message: 'Please fill out all required fields.' });
    return;
  }
  try {
    await ElMessageBox.confirm('Are you sure you want to update experiences?', 'Update Experiences', {
      confirmButtonText: 'Update',
      cancelButtonText: 'Cancel',
      customClass: 'exp-message-box'
    });
    let settingId = null;
    if(userRole === 'locum') {
        settingId = userData.value.locumAsJobSeekerSetting?.id;
    } else {
        settingId = userData.value.settings.id;
    }
    const response = await store.dispatch('UPDATE_JOB_SEEKER_EXPERIENCES', { params: editExperienceForm, id: settingId });

    if (response.status) {
      ElMessage({ type: 'success', message: response.message });
      store.dispatch('SET_CURRENT_USER_DATA');
      closeExperienceDialog();
    } else {
      ElMessage({ type: 'info', message: response.message });
    }
  } catch (error) {
    if (error.message) {
      ElMessage({ type: 'error', message: error.message });
    }
  }
};

const handleDateChange = (experience, field) => {
  if (!experience[field]) {
    experience[field] = ""; // Prevent it from becoming null
  }
};

const handleDateClear = (experience, field) => {
  experience[field] = ""; // Keep it as an empty string instead of removing fields
  console.log("🚀 ~ handleDateClear ~ experience[field]:", experience[field])
};

const closeExperienceDialog = () => {
  experiences.value = [];
  emit('closeExperienceDrawer', false);
};
</script>
<style lang="scss">
.el-overlay.is-message-box,
.el-message.el-message--error {
  z-index: 9999 !important;
}
</style>